import React, { useEffect } from "react";

import clsx from "clsx";
import _ from "lodash";
import moment from "moment";
import "moment-timezone";
import { Link, useLocation } from "react-router-dom";

import { usePageTitle } from "@/atoms/page-title-atom";
import { LoadingSlide } from "@components/title-slide-view";
import { H1, LightSwitch } from "@components/utilities";
import { LogOnMount } from "@components/v2/analytics";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type GetAllAlertConfigs,
  type GetAllAlertConfigs_me_alertConfigs as AlertConfigView,
  type ToggleAlertConfig,
  type ToggleAlertConfigVariables,
} from "@north-beam/nb-client-side";
import { AlertConfigStatus } from "@north-beam/nb-client-side";
import { type TriggerConditionsV2, errors } from "@north-beam/nb-common";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";

import {
  FormatAlarmCondition,
  FormatSubject,
} from "./components/humanize-fields";
import { GET_ALL_ALERT_CONFIGS, TOGGLE_ALERT_CONFIG } from "./queries";

export default function AlertConfigList() {
  const [, setPageTitle] = usePageTitle();
  useEffect(() => {
    setPageTitle("Monitor");
  }, [setPageTitle]);

  return (
    <div className="container pt-4">
      <LogOnMount name="Visit Alerts List Page" />
      <div className="row">
        <div className="col">
          <H1>Monitor</H1>
          <p>
            We&apos;ll keep a watch on your performance, so you don&apos;t have
            to.
          </p>
        </div>
      </div>
      <ListViewWithFilter />
    </div>
  );
}

function ListViewWithFilter() {
  const { pathname } = useLocation();
  const { loading, data } = useNorthbeamQuery<GetAllAlertConfigs>(
    GET_ALL_ALERT_CONFIGS,
    {},
  );
  const [tab, setTab] = React.useState<Tab>(TABS[0]);

  if (loading) {
    return <LoadingSlide />;
  }

  const allAlerts = data?.me.alertConfigs ?? [];

  const sortedAlerts = _.sortBy([...allAlerts], "createdAt").reverse();

  const alerts = sortedAlerts.filter((a) => {
    if (tab === "Active") {
      return a.status === "active";
    } else if (tab === "Inactive") {
      return a.status === "inactive";
    } else if (tab === "Active + Inactive") {
      return a.status === "active" || a.status === "inactive";
    } else if (tab === "Archived") {
      return a.status === "archived";
    } else if (tab === "All") {
      return true;
    } else {
      throw new errors.UnreachableCaseError(tab);
    }
  });

  _.sortBy(alerts, "createdAt");

  return (
    <div className="row" style={{ fontSize: "14px" }}>
      <div className="col">
        <nav className="navbar border-top border-left border-right">
          <ul className="nav nav-pills">
            {TABS.map((t) => (
              <li className="nav-item" key={t}>
                <SecondaryButton
                  className={clsx(
                    {
                      "bg-cosmic text-white": t === tab,
                    },
                    "mx-1",
                  )}
                  onClick={() => setTab(t)}
                >
                  {t}
                </SecondaryButton>
              </li>
            ))}
          </ul>
          <ul className="nav nav-right">
            <li className="nav-item">
              <Link to={`${pathname}/new`}>
                <PrimaryButton size="md">ADD ALERT</PrimaryButton>
              </Link>
            </li>
          </ul>
        </nav>
        <ListView alerts={alerts}>
          {(alert: AlertConfigView) => (
            <AlertConfigRow key={alert.id} pathname={pathname} alert={alert} />
          )}
        </ListView>
      </div>
    </div>
  );
}

interface TableViewProps {
  alerts: AlertConfigView[];
  children: (alert: AlertConfigView) => React.ReactNode;
}

const TABS = [
  "Active",
  "Active + Inactive",
  "Inactive",
  "Archived",
  "All",
] as const;
type Tab = (typeof TABS)[number];

function ListView(props: TableViewProps) {
  const { alerts, children } = props;

  return (
    <table className="table table-bordered bg-white">
      <thead className="thead-light">
        <tr>
          <th style={{ textAlign: "center" }} scope="col">
            Active?
          </th>
          <th scope="col">Name</th>
          <th scope="col">Created</th>
          <th scope="col">Subjects</th>
          <th scope="col">Alarm Conditions</th>
          <th style={{ textAlign: "center" }} scope="col">
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        {alerts.length === 0 ? (
          <tr>
            <td colSpan={7} align="center">
              No alerts. Click the ADD ALERT button to make one!
            </td>
          </tr>
        ) : (
          alerts.map(children)
        )}
      </tbody>
    </table>
  );
}

interface AlertConfigRowProps {
  pathname: string;
  alert: AlertConfigView;
}

function AlertConfigRow({ pathname, alert }: AlertConfigRowProps) {
  const { id, status, createdAt, name } = alert;
  const triggerConditions: TriggerConditionsV2 = alert.triggerConditions;
  const { subject, alarmCondition } = triggerConditions;

  const [toggleAlertConfig] = useNorthbeamMutation<
    ToggleAlertConfig,
    ToggleAlertConfigVariables
  >(TOGGLE_ALERT_CONFIG, {
    refetchQueries: [{ query: GET_ALL_ALERT_CONFIGS }],
  });

  const alertToggle = React.useCallback(() => {
    const newStatus =
      status === AlertConfigStatus.inactive
        ? AlertConfigStatus.active
        : status === AlertConfigStatus.active
        ? AlertConfigStatus.inactive
        : AlertConfigStatus.inactive;
    toggleAlertConfig({
      variables: {
        id: id,
        status: newStatus,
      },
    });
  }, [toggleAlertConfig, id, status]);

  const archiveAlert = React.useCallback(() => {
    toggleAlertConfig({
      variables: {
        id: id,
        status: AlertConfigStatus.archived,
      },
    });
  }, [toggleAlertConfig, id]);

  const nameElement =
    status === "archived" ? (
      <span>{name}</span>
    ) : (
      <Link to={`${pathname}/view/${id}`}>{name}</Link>
    );

  return (
    <tr key={id}>
      <td align="center">
        <div className="flex justify-content-center">
          <LightSwitch
            size="medium"
            disabled={status === "archived"}
            isSet={status === "active"}
            onChange={alertToggle}
            id={id}
          />
        </div>
      </td>
      <td>{nameElement}</td>
      <td>{moment(createdAt).local().format("MMM D, YYYY")}</td>
      <td>
        <FormatSubject subject={subject} />
      </td>
      <td>
        <FormatAlarmCondition alarmCondition={alarmCondition} />
      </td>
      <td align="center">
        <div className="flex justify-content-center">
          {status !== "archived" ? (
            <SecondaryButton onClick={archiveAlert}>
              <i className="fa-regular fa-sharp fa-archive" title="Archive" />
            </SecondaryButton>
          ) : (
            <SecondaryButton onClick={alertToggle}>
              <i
                className="fa-regular fa-sharp fa-trash-restore"
                title="Restore from archive"
              />
            </SecondaryButton>
          )}
        </div>
      </td>
    </tr>
  );
}
